import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  MapPin, LogOut, Inbox, Mail, Users, Wrench, HelpCircle, Quote,
  Plus, Pencil, Trash2, X, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import api from '../lib/adminApi';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const EmptyState = ({ icon: Icon, title, desc }) => (
  <div className="text-center py-16 text-slate-400">
    <Icon className="w-10 h-10 mx-auto mb-3 opacity-60" />
    <div className="font-semibold text-slate-500">{title}</div>
    <div className="text-sm">{desc}</div>
  </div>
);

/* ---------- Editor Modal ---------- */
const Editor = ({ title, fields, initial, onClose, onSave }) => {
  const [values, setValues] = useState(initial || {});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(values);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-[#0b2f5c]">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{f.label}</label>
              {f.type === 'textarea' ? (
                <Textarea
                  rows={3}
                  value={values[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="focus-visible:ring-sky-400"
                />
              ) : (
                <Input
                  type={f.type || 'text'}
                  value={values[f.key] ?? ''}
                  onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                  placeholder={f.placeholder}
                  className="focus-visible:ring-sky-400"
                />
              )}
              {f.hint && <div className="text-[10px] text-slate-400 mt-1">{f.hint}</div>}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0b2f5c] hover:bg-[#0f3a70] text-white">
            {saving ? 'Saving…' : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------- CRUD Section ---------- */
const CrudSection = ({
  label, icon: Icon, items, columns, fields,
  onCreate, onUpdate, onDelete, refresh,
  newTitle, editTitle, emptyDesc,
}) => {
  const { toast } = useToast();
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {..} = edit

  const save = async (values) => {
    try {
      if (editing && editing.id) await onUpdate(editing.id, values);
      else await onCreate(values);
      toast({ title: 'Saved' });
      refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Please try again.' });
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await onDelete(id);
      toast({ title: 'Deleted' });
      refresh();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-sky-500" />
          <h2 className="text-lg font-bold text-[#0b2f5c]">{label}</h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{items.length}</span>
        </div>
        <Button onClick={() => setEditing({})} className="bg-sky-500 hover:bg-sky-600 text-white h-9">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Icon} title="Nothing here yet" desc={emptyDesc} />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-4 py-3 font-semibold">{c.label}</th>
                ))}
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 align-top text-slate-700">
                      {c.render ? c.render(it) : (it[c.key] || '—')}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditing(it)}
                        className="p-1.5 rounded-lg hover:bg-sky-50 text-sky-600"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => del(it.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <Editor
          title={editing.id ? editTitle : newTitle}
          fields={fields}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
};

/* ---------- Dashboard ---------- */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [messages, setMessages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [content, setContent] = useState({ services: [], faqs: [], testimonials: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, w, c] = await Promise.all([
        api.listMessages(),
        api.listWaitlist(),
        api.getContent(),
      ]);
      setMessages(m);
      setWaitlist(w);
      setContent(c);
    } catch (err) {
      if (err?.response?.status === 401) {
        api.clearToken();
        navigate('/admin/login');
      } else {
        toast({ title: 'Could not load data', description: err?.message || 'Try again.' });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => { load(); }, [load]);

  if (!api.isAuthed()) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    api.clearToken();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-300 to-sky-500 flex items-center justify-center shadow">
              <MapPin className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0b2f5c] leading-tight">MaLife Admin</div>
              <div className="text-[10px] text-slate-500">Matshelonyana · Control panel</div>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="h-9">
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Messages', value: messages.length, icon: Inbox },
            { label: 'Waitlist', value: waitlist.length, icon: Users },
            { label: 'Services', value: content.services.length, icon: Wrench },
            { label: 'FAQs', value: content.faqs.length, icon: HelpCircle },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl bg-sky-100 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-sky-600" />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Total</span>
              </div>
              <div className="text-2xl font-bold text-[#0b2f5c]">{loading ? '–' : s.value}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="messages">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto">
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          </TabsList>

          <TabsContent value="messages" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Inbox className="w-5 h-5 text-sky-500" />
              <h2 className="text-lg font-bold text-[#0b2f5c]">Contact messages</h2>
            </div>
            {messages.length === 0 ? (
              <EmptyState icon={Mail} title="No messages yet" desc="Submissions from the contact form will appear here." />
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-bold text-[#0b2f5c]">{m.name}</div>
                        <a href={`mailto:${m.email}`} className="text-xs text-sky-600 hover:underline">{m.email}</a>
                      </div>
                      <div className="text-[11px] text-slate-400">{formatDate(m.created_at)}</div>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="waitlist" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-sky-500" />
              <h2 className="text-lg font-bold text-[#0b2f5c]">Waitlist signups</h2>
            </div>
            {waitlist.length === 0 ? (
              <EmptyState icon={Users} title="No signups yet" desc="Signups from the landing page will appear here." />
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Email</th>
                      <th className="text-left px-4 py-3 font-semibold">City</th>
                      <th className="text-left px-4 py-3 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map((w) => (
                      <tr key={w.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-medium text-[#0b2f5c]">{w.email}</td>
                        <td className="px-4 py-3 text-slate-600">{w.city || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(w.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <CrudSection
              label="Services"
              icon={Wrench}
              items={content.services}
              refresh={load}
              newTitle="New service"
              editTitle="Edit service"
              emptyDesc="Add your first service."
              columns={[
                { key: 'title', label: 'Title', render: (i) => <span className="font-semibold text-[#0b2f5c]">{i.title}</span> },
                { key: 'tagline', label: 'Tagline' },
                { key: 'icon', label: 'Icon' },
              ]}
              fields={[
                { key: 'title', label: 'Title' },
                { key: 'tagline', label: 'Tagline' },
                { key: 'desc', label: 'Description', type: 'textarea' },
                { key: 'icon', label: 'Icon', placeholder: 'Car | Package | Wrench | ShieldCheck', hint: 'Use one of: Car, Package, Wrench, ShieldCheck' },
              ]}
              onCreate={api.createService}
              onUpdate={api.updateService}
              onDelete={api.deleteService}
            />
          </TabsContent>

          <TabsContent value="faqs" className="mt-6">
            <CrudSection
              label="FAQs"
              icon={HelpCircle}
              items={content.faqs}
              refresh={load}
              newTitle="New FAQ"
              editTitle="Edit FAQ"
              emptyDesc="Add your first question."
              columns={[
                { key: 'q', label: 'Question', render: (i) => <span className="font-semibold text-[#0b2f5c]">{i.q}</span> },
                { key: 'a', label: 'Answer', render: (i) => <span className="text-slate-600 line-clamp-2">{i.a}</span> },
              ]}
              fields={[
                { key: 'q', label: 'Question' },
                { key: 'a', label: 'Answer', type: 'textarea' },
              ]}
              onCreate={api.createFaq}
              onUpdate={api.updateFaq}
              onDelete={api.deleteFaq}
            />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <CrudSection
              label="Testimonials"
              icon={Quote}
              items={content.testimonials}
              refresh={load}
              newTitle="New testimonial"
              editTitle="Edit testimonial"
              emptyDesc="Add your first testimonial."
              columns={[
                { key: 'name', label: 'Name', render: (i) => <span className="font-semibold text-[#0b2f5c]">{i.name}</span> },
                { key: 'role', label: 'Role / City' },
                { key: 'quote', label: 'Quote', render: (i) => <span className="text-slate-600 line-clamp-2">“{i.quote}”</span> },
                { key: 'rating', label: 'Rating' },
              ]}
              fields={[
                { key: 'name', label: 'Name' },
                { key: 'role', label: 'Role / City' },
                { key: 'quote', label: 'Quote', type: 'textarea' },
                { key: 'rating', label: 'Rating (1-5)', type: 'number', placeholder: '5' },
              ]}
              onCreate={api.createTestimonial}
              onUpdate={api.updateTestimonial}
              onDelete={api.deleteTestimonial}
            />
          </TabsContent>
        </Tabs>
      </main>
      <Toaster />
    </div>
  );
};

export default AdminDashboard;
